import { computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { type SmxMenuItem, useMenuStore } from "../../stores/menu";
import { useAuthStore } from "../../stores/auth";
import { clientId } from "../constants";
import { useMaintenanceReportGroupsStore } from "../stores/reportGroups";

export function useTicketManagementMenu() {
  const { t } = useI18n();

  const menuStore = useMenuStore();
  const authStore = useAuthStore();
  const reportGroupsStore = useMaintenanceReportGroupsStore();

  const ticketManagementMenu = computed(() => {
    const content: SmxMenuItem[] = [];

    if (authStore.rolesByClientId[clientId]?.roles.includes("admin")) {
      content.push({
        id: "tm-admin",
        name: t("ticketAdmin"),
        type: "routerlink",
        route: { name: "tmTicketsAdmin" },
      });
    }

    content.push({
      id: "tm-search",
      name: t("ticketSearch"),
      type: "routerlink",
      route: { name: "tmTicketsSearch" },
    });

    content.push({
      id: "tm-calendar",
      name: t("calendar"),
      type: "routerlink",
      route: { name: "tmMaintenanceCalendar" },
    });

    content.push({
      id: "tm-myTickets",
      name: t("myTickets"),
      type: "routerlink",
      route: { name: "tmMyTickets" },
    });

    if (
      authStore.rolesByClientId[clientId]?.roles.includes("admin") ||
      authStore.rolesByClientId[clientId]?.roles.includes("ticket-creator")
    ) {
      content.push({
        id: "tm-create-ticket",
        name: t("createTicket"),
        type: "routerlink",
        route: { name: "tmCreateTicket" },
      });
    }

    if (authStore.rolesByClientId[clientId]?.roles.includes("admin")) {
      content.push({
        id: "tm-plans",
        name: t("maintenancePlans"),
        type: "routerlink",
        route: { name: "tmMaintenancePlans" },
      });
    }

    if (authStore.rolesByClientId[clientId]?.roles.includes("admin")) {
      content.push({
        id: "tm-sop",
        name: t("sops"),
        type: "routerlink",
        route: { name: "tmSop" },
      });

      content.push({
        id: "tm-machine-restrictions",
        name: t("machineRestrictions"),
        type: "routerlink",
        route: { name: "tmMachineRestrictions" },
      });

      const reportChildren: SmxMenuItem[] = [
        {
          id: "reports",
          name: t("changelogs"),
          type: "routerlink",
          route: { name: "tmReports" },
        },
        {
          id: "reports-machine",
          name: t("maintenanceReports"),
          type: "routerlink",
          route: { name: "tmMachineReports" },
        },
        ...reportGroupsStore.reportGroups.map<SmxMenuItem>((group) => ({
          id: `tm-custom-report-${group.id}`,
          name: group.name,
          type: "routerlink",
          route: {
            name: "tmCustomReport",
            params: { groupId: group.id },
          },
        })),
      ];

      content.push({
        id: "tm-reports",
        name: t("reports"),
        type: "routerlink",
        isDropDown: true,
        route: { name: "tmReportsOverview" },
        children: reportChildren,
      });

      content.push({
        id: "tm-dashboard",
        name: t("dashboards"),
        type: "routerlink",
        route: { name: "tmDashboardList" },
      });
    }

    return { content };
  });

  function setMenu() {
    menuStore.setMenu(ticketManagementMenu.value);
  }

  watch(ticketManagementMenu, setMenu, { deep: true });

  watch(() => reportGroupsStore.reportGroups, setMenu);

  onMounted(() => {
    void reportGroupsStore.loadMaintenanceReportGroups();
    setMenu();
  });
}
